import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { EntityEvent, EntityEventTypeEnum, EntityIssue, JoDetail, Manifest, PoDetail } from "app/model/entities/entity-model";
import { DataProperty, Entity, EntityState, SaveOptions } from "breeze-client";
import _ from "lodash";
import { DialogService } from "primeng/dynamicdialog";
import { firstValueFrom, lastValueFrom } from "rxjs";
import { AuthService } from "./auth.service";

import { DbQueryService } from "./db-query.service";
import { DialogWrapperService } from "./dialog-wrapper.service";
import { UnitOfWork } from "./unit-of-work";
import { ValidationSaveErrorsDialogComponent, ValidationSaveErrorsDialogParams } from "./validation-save-errors-dialog.component";
import { BusyService } from "./busy.service";
import { environment } from "environments/environment";
import { ToastrService } from "ngx-toastr";

export type EntityValues<T extends Entity> = Partial<Omit<T, keyof Entity>>;

export interface ProximityPostResult {
  HttpStatusCode: number;
  ErrorMessage?: string;
  ConfirmationKey?: string;
}

export interface ProximityBatchResult {
  Ok: boolean;
  HttpStatusCode: number;
  ErrorMessage?: string;
  Count: number;
}

@Injectable({ providedIn: "root" })
export class DbSaveService {
  constructor(
    public uow: UnitOfWork,
    private http: HttpClient,
    public dbQueryService: DbQueryService,
    public dialogWrapperService: DialogWrapperService,
    public busyService: BusyService,
    public authService: AuthService,
    public pngDialogService: DialogService,
    public toastr: ToastrService
  ) {}

  public busyPromise: Promise<any> | undefined;

  public isBusy() {
    // DON'T add this.busyService.isBusy() here, it will cause ExpressionChangedAfterItHasBeenCheckedError in some cases.
    return this.busyPromise != null; //  || this.busyService.isBusy();
  }

  async wrapBusy<T>(op: Promise<T> | (() => Promise<T>)) {
    if (this.busyPromise) {
      return await (typeof op === "function" ? op() : op);
    }

    try {
      this.busyPromise = typeof op === "function" ? op() : op;
      return await this.busyPromise;
    } finally {
      this.busyPromise = undefined;
    }
  }

  rejectChanges() {
    return this.uow.rejectChanges();
  }

  hasChanges() {
    return this.uow.manager.hasChanges();
  }

  getChanges(): Entity[] {
    return this.uow.manager.getChanges();
  }

  getChangesOfType<T extends Entity>(type: { new (): T }): T[] {
    return this.uow.manager.getChanges(type.name) as T[];
  }

  async sendProximitySupplierDataByBatch() {
    const r = await this.postData<ProximityBatchResult>("SendProximitySupplierDataByBatch", null, {});
    return r;
  }

  async sendProximityShipment(shipmentId: number) {
    const r = await this.postData<ProximityPostResult>("AddProximityShipment", { id: shipmentId }, null);
    return r;
  }

  async sendProximityInvoice(invoiceHeaderId: string) {
    const r = await this.postData<ProximityPostResult>("AddProximityInvoice", { id: invoiceHeaderId }, null);
    return r;
  }

  async combineFeatureChoices(fromFeatureChoiceId: number, toFeatureChoiceId: number) {
    const q = this.uow
      .createQuery(null, "CombineFeatureChoices") // method on server
      .withParameters({ fromFeatureChoiceId, toFeatureChoiceId });
    const r = await q.execute(true);
    return _.first(r) as unknown as number;
  }

  async combineAddons(fromAddonId: number, toAddonId: number) {
    const q = this.uow
      .createQuery(null, "CombineAddons")
      .withParameters({ fromAddonId, toAddonId });
    const r = await q.execute(true);
    return _.first(r) as unknown as number;
  }

  async previewApplyCoreFeatureChoices(featureId: number, featureChoiceIds: number[]) {
    const q = this.uow
      .createQuery(null, "PreviewApplyCoreFeatureChoices")
      .withParameters({ featureId, featureChoiceIds: featureChoiceIds.join(",") });
    const r = await q.execute(true);
    return r as unknown as CoreChoicePreviewItem[];
  }

  async applyCoreFeatureChoices(featureId: number, featureChoiceIds: number[], productTypeIds: number[]) {
    return this.postData<{ ProductsCreated: number; ProductTypesAffected: number }>(
      "ApplyCoreFeatureChoices",
      { featureId, featureChoiceIds, productTypeIds }
    );
  }

  async combineProducts(fromProductIds: number[], toProductId: number) {
    const q = this.uow
      .createQuery(null, "CombineProducts") // method on server
      .withParameters({ fromProductIds, toProductId });
    const r = await q.execute(true);
    return _.first(r) as unknown as number;
  }

  async addEntityIssue(issueStruct: EntityIssue) {
    issueStruct.crtnTs = new Date();
    issueStruct.crtnUserInit = this.authService.getUser().initials;
    const issue = this.uow.createEntity(EntityIssue, issueStruct);
    await this.saveChanges();
  }

  createEntityEvent(eeStruct: Partial<EntityEvent>) {
    eeStruct.crtnTs = new Date();
    eeStruct.crtnUserInit = this.authService.getUser()?.initials || 'EXT';
    const ee = this.uow.createEntity(EntityEvent, eeStruct);
    return ee;
  }

  // async migrateFeatureChoice(fromFeatureChoiceId: number, toFeatureId: number) {
  //   const q = this.uow
  //     .createQuery(null, 'MigrateFeatureChoice') // method on server
  //     .withParameters({ fromFeatureChoiceId, toFeatureId });
  //   const r = await q.execute(true);
  //   return _.first(r) as unknown as number;
  // }

  // the manifest itself may have a rule change at this point - so we don't want to query it.
  async updateManifestAndRelated(manifest: Manifest) {
    const nextProcessDate = await this.dbQueryService.getManifestNextProcessDate(manifest);
    manifest.nextProcessDate = nextProcessDate;
    const entityIssues = await this.dbQueryService.getEntityIssues("Manifest", manifest.proximityManifestId);
    // check if EntityIssue exists for missing schedule and delete it.
    // If new entityIssue records are needed they will be added later.
    entityIssues.forEach((ei) => {
      ei.entityAspect.setDeleted();
    });

    await this.saveChanges();

    const newEntityIssues = await this.updateManifestIssues();
  }

  async updateManifestIssues() {
    const q = this.uow.createQuery(EntityIssue, "UpdateManifestIssues");

    const r = await q.execute(true);
    return r;
  }

  createEntity<T extends Entity>(type: { new (): T }, initalValues?: EntityValues<T>, entityState?: EntityState): T {
    return this.uow.createEntity(type, initalValues, entityState);
  }

  createOrUndoEntity<T extends Entity>(type: { new (): T }, keyObj: EntityValues<T>): T {
    const kps = type.prototype.entityType.keyProperties as DataProperty[];
    const keyValues = kps.map((kp) => keyObj[kp.name]);

    let ent = this.uow.undoIfDeleted(type, keyValues);
    if (ent == null) {
      ent = this.createEntity(type, keyObj);
    }
    return ent;
  }

  async saveChanges(shouldRollbackOnError = true) {
    return this.wrapBusy(async () => {
      try {
        return await this.uow.saveChanges();
      } catch (e: any) {
        if (shouldRollbackOnError) {
          this.uow.rollback();
        }
        if (e.message) {
          console.error(e.message);
        }
        if (e.entityErrors) {
          console.error(e.entityErrors);
        }
        throw e;
      }
    });
  }

  async saveSelectedChanges(entities: Entity[], shouldRollbackOnError = true) {
    return this.wrapBusy(async () => {
      try {
        return await this.uow.saveChanges(entities);
      } catch (e: any) {
        if (shouldRollbackOnError) {
          this.uow.rollback();
        }
        if (e.entityErrors) {
          console.error(e.entityErrors);
        }
        throw e;
      }
    });
  }

  /**
   * Saves only these entities. If the save fails, only these are undone; every other pending change
   * stays as the operator left it. For a dialog or a small action on a page that can hold other
   * unsaved edits, where saveSelectedChanges would roll back the whole page on a refusal.
   */
  async saveOwnChanges(entities: Entity[]) {
    try {
      return await this.saveSelectedChanges(entities, false);
    } catch (e) {
      entities.filter((x) => !x.entityAspect.isBeingSaved).forEach((x) => x.entityAspect.rejectChanges());
      throw e;
    }
  }

  /** Save changes using anonymous endpoint for external manufacturer backorder updates */
  async saveChangesAnonymous(resourceName: string, shouldRollbackOnError = true) {
    return this.wrapBusy(async () => {
      try {
        const saveOptions = new SaveOptions({ resourceName });
        return await this.uow.manager.saveChanges(null, saveOptions);
      } catch (e: any) {
        if (shouldRollbackOnError) {
          this.uow.rollback();
        }
        if (e.message) {
          console.error(e.message);
        }
        if (e.entityErrors) {
          console.error(e.entityErrors);
        }
        throw e;
      }
    });
  }

  // cannot use the DialogBaseComponent.open because the DialogBaseComponent component has initialization issues with the dbSaveService where
  // this component is used.  So we have to handle initialization differently in this one case.
  async openValidationErrorDialog(params: ValidationSaveErrorsDialogParams) {
    const fullConfig = {
      closable: false,
      draggable: true,
      resizeable: false,
      width: "calc(60vw)",
      height: "calc(60vh)",
      ...params,
      data: params,
    };

    const ref = this.pngDialogService.open(ValidationSaveErrorsDialogComponent, fullConfig);

    const r = await lastValueFrom(ref.onClose);
    return r;
  }

  postData<TResponse>(action: string, body: any, params?: object): Promise<TResponse> {
    const url = action.startsWith("http") ? action : this.uow.manager.serviceName + action;
    return this.busyService.wrapBusy(async () => {
      return firstValueFrom(this.http.post<TResponse>(url, body, params));
    });
  }

  // async updateManifestEntityIssues() {
  //   const manifests = await this.dbQueryService.getManifestsThatAreCurrentlyDue();
  //   const promises = manifests.map(m => {
  //     const issues  = this.dbQueryService.getEntityIssues('Manifest', m.proximityManifestId);
  //   });
  // }

  // createManifestIssue(manifest: Manifest) {
  //   const issueStruct = <EntityIssue> {};
  //   issueStruct.entityTypeName = 'Manifest';
  //   issueStruct.entityId =  manifest.proximityManifestId;
  //   issueStruct.accountId = manifest.joHeaders[0].accountId;
  //   issueStruct.description = 'This Manifest is now due for invoicing, as of: ' + UtilFns.fmtDate4(manifest.nextProcessDate);
  //   issueStruct.crtnTs = new Date();
  //   issueStruct.crtnUserInit = this.authService.getUser().initials;
  //   issueStruct.warningTypeId = WarningTypeCode.Warning;
  //   const issue = this.uow.createEntity(EntityIssue, issueStruct);
  // }
}

export interface CoreChoicePreviewItem {
  productTypeId: number;
  manufacturerName: string;
  sku: string;
  description: string;
  missingChoices: string;
  productCount: number;
}
